import { useCallback } from 'react';
import { useNotify } from 'react-admin';
import { Editor } from '@tiptap/react';
import { EditorialAssetSearchResultItem } from 'utility/types/dataProvider';
import { createBloxInlineNode } from '../../../article/InlineAssets/extensions/BloxInline';

/**

 * Hook to handle insertion of selected items into articles or collections
 * Provides unified insert logic for the relationships search modal
 */
export const useRelationshipsInsert = (
  contentEditor: Editor | null | undefined,
  onInsertIntoCollection?: (args: {
    records: EditorialAssetSearchResultItem[];
    mainRecordUUID: string;
  }) => void,
  recordUuid?: string,
  replaceNodePosition?: number,
  onClose?: () => void,
) => {
  const notify = useNotify();

  /**
   * Insert items into article by replacing a placeholder node
   */
  const insertArticleInlineWithReplacement = useCallback(
    (items: EditorialAssetSearchResultItem[]) => {
      if (!contentEditor || typeof replaceNodePosition !== 'number') {
        return false;
      }

      const wasSuccessful = contentEditor
        .chain()
        .focus()
        .command(({ tr }) => {
          const oldNode = tr.doc.nodeAt(replaceNodePosition);
          if (!oldNode) {
            console.error(
              '[useRelationshipsInsert] No node found at position',
              replaceNodePosition,
            );
            return false;
          }

          // Delete the old placeholder node first
          const from = replaceNodePosition;
          const to = replaceNodePosition + oldNode.nodeSize;
          tr.delete(from, to);

          // Insert all selected items at the position where placeholder was
          let position = replaceNodePosition;
          items.forEach((record) => {
            const node = createBloxInlineNode(contentEditor, record);

            if (node && node.nodeSize && typeof position === 'number') {
              tr.insert(position, node);
              position += node.nodeSize;
            }
          });

          return tr.docChanged;
        })
        .focus()
        .run();

      if (wasSuccessful) {
        notify(`Added ${items.length} ${items.length === 1 ? 'item' : 'items'} inline.`, {
          type: 'success',
        });
        if (onClose) {
          onClose();
        }
      }

      return wasSuccessful;
    },
    [contentEditor, replaceNodePosition, notify, onClose],
  );

  /**
   * Insert items into article at current cursor position
   */
  const insertArticleInlineAtCursor = useCallback(
    (items: EditorialAssetSearchResultItem[]) => {
      if (!contentEditor) {
        return false;
      }

      // Get current cursor position
      let position = contentEditor.state.selection.anchor;

      // Insert the new bloxInline nodes at the cursor position
      const wasSuccessful = contentEditor
        .chain()
        .focus()
        .command(({ tr }) => {
          items.forEach((record) => {
            const node = createBloxInlineNode(contentEditor, record);

            if (node && node.nodeSize && typeof position === 'number') {
              tr.insert(position, node);
              position += node.nodeSize;
            }
          });

          return tr.docChanged;
        })
        .focus()
        .run();

      if (wasSuccessful) {
        notify(`Added ${items.length} ${items.length === 1 ? 'item' : 'items'} inline.`, {
          type: 'success',
        });
        if (onClose) {
          onClose();
        }
      }

      return wasSuccessful;
    },
    [contentEditor, notify, onClose],
  );

  /**
   * Main insert handler that routes to the appropriate insertion method
   */
  const handleInsert = useCallback(
    (items: EditorialAssetSearchResultItem[]) => {
      // Collection insertion
      if (onInsertIntoCollection && recordUuid) {
        onInsertIntoCollection({ records: items, mainRecordUUID: recordUuid });
        if (onClose) {
          onClose();
        }
        return true;
      }

      // Article insert with placeholder replacement or cursor position
      if (contentEditor && contentEditor.schema?.nodes?.bloxInline) {
        if (typeof replaceNodePosition === 'number') {
          return insertArticleInlineWithReplacement(items);
        }

        // Article insert at cursor position
        return insertArticleInlineAtCursor(items);
      }

      // No valid insert method available
      console.error('[useRelationshipsInsert] No valid insert method available', {
        hasContentEditor: Boolean(contentEditor),
        hasOnInsertIntoCollection: Boolean(onInsertIntoCollection),
      });
      return false;
    },
    [
      contentEditor,
      onInsertIntoCollection,
      recordUuid,
      replaceNodePosition,
      insertArticleInlineWithReplacement,
      insertArticleInlineAtCursor,
      onClose,
    ],
  );

  const hasInsertCapability = Boolean(contentEditor) || Boolean(onInsertIntoCollection);

  return {
    handleInsert,
    hasInsertCapability,
  };
};
